import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DropDownGrid from "../Grid/Components/DropDownGrid";
import {
    Box,
    MenuItem,
    ListItemIcon,
    Tooltip,
    Chip,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Cookies from "universal-cookie";
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import Search from "./Search";
import FormContainer from "./Form/FormContainer";
import Title from "./Title";
import { selectRoles } from "../../reduxStore/RolesReducer";
import accountAPI from "../../Network/Account";

const cookies = new Cookies();

export default function Accounts() {
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const [t] = useTranslation("common");
    const [rows, setRows] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);

    const [pageCount, setPageCount] = useState(0);
    const [openFilterColumn, setOpenFilterColumn] = useState(false);
    const [openFilterColumnValue, setOpenFilterColumnValue] = useState([]);
    const [elementClicked, setElementClicked] = useState(null);

    const [filters, setFilters] = useState({
        account_name: "",
        is_governmental: null,
        contractor_id: null,
        municipality_id: null,
        enabled: null,
    })
    const [reload, setReload] = useState(false)

    const [openForm, setOpenForm] = useState(false);
    const [selectedObject, setselectedObject] = useState(null);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage + 1);
    };

    const navigate = useNavigate();
    const controller = new AbortController();
    const [rowsTotal, setRowsTotal] = useState(0);
    const screenwidth = useSelector((state) => state.settingsData.screenwidth);

    const rolesRedux = useSelector(selectRoles)

    const columns = [
        { field: "account_id", headerName: t("ACCOUNTS.id") || "ID", minWidth: 80, maxWidth: 120 },
        { field: "account_name", headerName: t("ACCOUNTS.name") || "اسم الحساب", minWidth: 150, maxWidth: 250, flex: 2 },
        {
            field: "is_governmental",
            headerName: t("ACCOUNTS.is_governmental") || "حكومي",
            minWidth: 100,
            maxWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.value ? (t("GLOBAL.yes") || "نعم") : (t("GLOBAL.no") || "لا")}
                    color={params.value ? "primary" : "default"}
                    size="small"
                />
            )
        },
        { field: "contractor_name", headerName: t("ACCOUNTS.contractor") || "المتعهد", minWidth: 120, maxWidth: 200, flex: 1.5 },
        { field: "municipality_name", headerName: t("ACCOUNTS.municipality") || "البلدية", minWidth: 120, maxWidth: 200, flex: 1.5 },
        {
            headerName: t("GLOBAL.actions") || "الإجراءات",
            field: "Actions",
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (
                <DropDownGrid>
                    {rolesRedux?.update_accounts?.value || rolesRedux?.update_master?.value ? (
                        <MenuItem onClick={() => updateFunction(params.row)} className="ActionIcons">
                            <ListItemIcon>
                                <EditIcon style={{ fill: maintheme?.primary }} />
                            </ListItemIcon>
                            {t("GLOBAL.action_edit")}
                        </MenuItem>
                    ) : null}
                    {rolesRedux?.delete_accounts?.value || rolesRedux?.delete_master?.value ? (
                        <MenuItem onClick={() => deleteFunction(params.row)} className="ActionIcons">
                            <ListItemIcon>
                                <DeleteIcon style={{ fill: "#d32f2f" }} />
                            </ListItemIcon>
                            {t("GLOBAL.action_delete")}
                        </MenuItem>
                    ) : null}
                </DropDownGrid>
            ),
        },
    ]

    useEffect(() => {
        loadData()
    }, [pageNumber, pageSize, reload]);

    const loadData = async (searchParams) => {
        if (cookies.get("active_session") === "yes") {
            let params = {
                username: cookies.get("user_name") ? cookies.get("user_name") : null,
                user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
                account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
                account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
                authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
                sid: cookies.get("sid") ? cookies.get("sid") : null,
                baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
                id: cookies.get("id") ? cookies.get("id") : null,
                page: pageNumber,
                page_size: pageSize,
                search_account_name: filters.account_name,
                is_governmental: filters.is_governmental,
                contractor_id: filters.contractor_id,
                municipality_id: filters.municipality_id,
                enabled: filters.enabled,
                ...searchParams
            };

            setLoading(true);
            const result = await accountAPI.getAccounts({
                params: params,
                signal: controller?.signal,
            });
            setLoading(false);

            if (result.status && result?.data?.status) {
                setRows((result?.data?.data?.data || []).map(row => ({ ...row, id: row.account_id })));
                setRowsTotal(result?.data?.data?.total_records || 0);
                setPageCount(Math.ceil((result?.data?.data?.total_records || result?.data?.data?.data?.length) / pageSize));
            } else {
                toast.error(result?.data?.data?.message || t("NETWORK_MESSAGES.unknownError"));
            }
        } else {
            navigate("/redirect");
        }
    };

    const createFunction = () => {
        setselectedObject(null);
        setOpenForm(true);
    };

    const updateFunction = (obj) => {
        setselectedObject(obj);
        setOpenForm(true);
    };

    const deleteFunction = async (obj) => {
        if (window.confirm(t("GLOBAL.confirm_delete") || "هل انت متأكد من الحذف؟")) {
            setLoading(true);
            const result = await accountAPI.deleteAccount({ 
                id: obj.account_id,
                params: {
                    username: cookies.get("user_name") ? cookies.get("user_name") : null,
                    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
                    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
                    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
                    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
                    sid: cookies.get("sid") ? cookies.get("sid") : null,
                    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
                    id: cookies.get("id") ? cookies.get("id") : null,
                }
            });
            setLoading(false);
            if (result.status && result.data.status) {
                toast.success(t("NETWORK_MESSAGES.deleteSuccess") || "تم الحذف بنجاح");
                loadData();
            } else {
                toast.error(result.data.data.message || t("NETWORK_MESSAGES.unknownError"));
            }
        }
    };

    const clearFilterFunction = () => {
        setFilters({
            account_name: "",
            is_governmental: null,
            contractor_id: null,
            municipality_id: null,
            enabled: null,
        })
    }

    const searchFunction = () => {
        setReload(!reload)
        setPageNumber(1)
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", overflowX: "hidden" }}>
            {openForm && (
                <FormContainer
                    object={selectedObject}
                    openForm={openForm}
                    setOpenForm={setOpenForm}
                    loadData={loadData}
                />
            )}
            <Grid
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                setPageSize={setPageSize}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                loading={loading}
                handlePageChange={handlePageChange}
                rowsTotal={rowsTotal}
                checkScreenSize={screenwidth}
                pageCount={pageCount}
                
                openFilterColumn={openFilterColumn}
                setOpenFilterColumn={setOpenFilterColumn}
                clearFunction={clearFilterFunction}
                searchFunction={searchFunction}
                hasCreate={false}
                LeftHeaderComponent={
                    <Title
                        createFunction={createFunction}
                        openFilterColumn={openFilterColumn}
                    />
                }
                filterChilds={
                    <Search
                        filters={filters}
                        setFilters={setFilters}
                    />
                }
            />
        </Box>
    );
}
